'use client';

import { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';

import { defaultDataTagGroup } from '@/constants/defaultSeo';

import { PromoCountDownComponent } from './PromoCountDown';

import { IComponentPromoStripMarquee } from '@/types/cms';
import { getFacetCode } from '@lamesarv-sdk/tools';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

enum AlignmentType {
  centered = 'centered',
  expanded = 'expanded',
}

enum ActionsType {
  link = 'link',
  button = 'button',
}

export const PromoStripMarquee = ({
  alignment = AlignmentType.centered,
  actions,
  classes,
  classes_actions,
  classes_timer,
  classes_timer_item,
  classes_timer_numbers,
  classes_timer_labels,
  classes_title,
  data_tag,
  data_tag_group,
  timer_date_time_end,
  title,
  title_target_blank,
  title_url,
}: IComponentPromoStripMarquee) => {
  return (
    <div className={twMerge('w-full bg-black-600', classes)}>
      <div
        className={twMerge(
          styles.container,
          alignment === AlignmentType.expanded ? styles.containerExpanded : styles.containerCentered,
        )}
      >
        {title && (
          <Fragment>
            {title_url ? (
              <a
                href={title_url}
                target={title_target_blank ? '_blank' : '_self'}
                className={twMerge(styles.title, 'underline', classes_title)}
                data-tag={data_tag ? `${data_tag}--promo-strip-marquee-title` : 'promo-strip-marquee-title'}
                data-tag-group={data_tag_group || defaultDataTagGroup}
              >
                {title}
              </a>
            ) : (
              <div className={twMerge(styles.title, classes_title)}>{title}</div>
            )}
          </Fragment>
        )}
        {timer_date_time_end && (
          <PromoCountDownComponent
            endDate={timer_date_time_end}
            className={classes_timer}
            classItem={classes_timer_item}
            classContent={classes_timer_numbers}
            classTitle={classes_timer_labels}
          />
        )}
        {actions && (
          <div className={twMerge(styles.containerActions, classes_actions)}>
            {actions?.map((action) => {
              const key = getFacetCode(action.title);

              return (
                <a
                  key={key}
                  href={action.url}
                  target={action.target_blank ? '_blank' : '_self'}
                  className={twMerge(
                    action.type === ActionsType.button ? styles.actionButton : styles.actionLink,
                    action.classes,
                  )}
                  data-tag={
                    data_tag ? `${data_tag}--promo-strip-marquee-action--${key}` : `promo-strip-marquee-action--${key}`
                  }
                  data-tag-group={
                    data_tag_group ? `${data_tag_group}--promo-strip-marquee-actions` : 'promo-strip-marquee-actions'
                  }
                >
                  {action.title}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: 'container mx-auto flex flex-col md:flex-row gap-2 md:gap-10 items-center p-3 text-center font-helvetica',
  containerCentered: 'justify-center',
  containerExpanded: 'justify-between',
  containerActions: 'flex flex-row flex-wrap gap-2 md:gap-6 justify-center items-center',
  title: 'text-[14px] md:text-[16px] text-white leading-none',
  actionLink: 'text-[12px] underline text-white leading-none',
  actionButton: 'text-[16px] text-white py-2 px-4 rounded-full leading-none bg-black-500 border border-black-350',
};
